<script setup>
import { MenuItem } from '@headlessui/vue';
import { Link as InertiaLink } from '@inertiajs/vue3';
import { computed } from 'vue';

defineOptions({ inheritAttrs: false });

const props = defineProps({
    label: {
        type: String,
        default: '',
    },
    href: {
        type: String,
        default: null,
    },
    icon: {
        type: [Function, Object],
        default: null,
    },
    variant: {
        type: String,
        default: 'default',
        validator: (v) => ['default', 'destructive'].includes(v),
    },
    type: {
        type: String,
        default: 'button',
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    external: {
        type: Boolean,
        default: false,
    },
});

const defaultType = computed(() => {
    if (props.href) {
        return props.type === 'button' ? undefined : props.type;
    }

    return props.type;
});

const resolvedComponent = computed(() => {
    if (props.disabled) {
        return props.href ? 'span' : 'button';
    }

    if (props.href && props.external) {
        return 'a';
    }

    return props.href ? InertiaLink : 'button';
});
</script>

<template>
    <MenuItem
        v-slot="{ active }"
        as="template"
        :disabled="disabled"
    >
        <component
            :is="resolvedComponent"
            :disabled="disabled"
            :href="disabled ? undefined : href"
            class="flex w-full select-none items-center gap-x-2 whitespace-nowrap rounded-md px-2 py-1.5 text-left text-sm text-gray-900 transition-colors duration-100 data-[variant=destructive]:text-red-600 data-[variant=destructive]:hover:bg-red-600/10 data-[variant=destructive]:focus:bg-red-600/10"
            :class="[
                active && variant === 'default' ? 'bg-gray-100' : '',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            ]"
            :type="defaultType"
            :data-variant="variant"
            v-bind="$attrs"
        >
            <component
                :is="icon"
                v-if="icon"
                class="pointer-events-none size-4 shrink-0"
                :class="{
                    'opacity-60': variant === 'default'
                }"
                aria-hidden="true"
            />

            <slot>
                <span>{{ label }}</span>
            </slot>
        </component>
    </MenuItem>
</template>
